import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { message } from 'antd';
import { StyledNewListingContractTermsForm } from './style';
import { DatePicker } from 'ui/DatePicker';
import { TextArea } from 'ui/TextArea';
import { FileUploader } from 'ui/Upload';
import { UploadFile } from 'antd';
import { ListingContractDocument } from '../../../types/ListingData';
import { routes } from '../../../config/config';

interface NewListingContractTermsFormProps {
  initialDocuments?: ListingContractDocument[];
  onDocumentsChange?: (documents: ListingContractDocument[]) => void;
  listingId?: string | null;
  onDownloadFile?: (file: UploadFile) => void;
  onDeleteFile?: (file: UploadFile) => void;
}

interface CustomUploadFile extends UploadFile {
  file_path?: string;
  fileId?: number;
  created_at?: string;
  isExisting?: boolean;
  file_size?: number;
  file_format?: string;
}

export const NewListingContractTermsForm = ({
  initialDocuments,
  onDocumentsChange,
  listingId,
  onDownloadFile,
  onDeleteFile,
}: NewListingContractTermsFormProps) => {
  const intl = useIntl();
  const [documents, setDocuments] = useState<ListingContractDocument[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  useEffect(() => {
    if (Array.isArray(initialDocuments) && initialDocuments.length > 0) {
      setDocuments((prev) => {
        const prevIds = new Set(
          prev.map((c) => c.file_id || c.id).filter(Boolean)
        );
        const newIds = new Set(
          initialDocuments.map((c) => c.file_id || c.id).filter(Boolean)
        );
        const idsMatch =
          prevIds.size === newIds.size &&
          Array.from(prevIds).every((id) => newIds.has(id));

        if (idsMatch) {
          return prev;
        }

        return initialDocuments;
      });

      const existingFiles: UploadFile[] = initialDocuments.map((c) => {
        const fileObj = c.file;
        const name =
          c.file_name ||
          fileObj?.original_name ||
          fileObj?.name ||
          `contract_${c.file_id || ''}`;
        const size = c.file_size || fileObj?.size_bytes;
        return {
          uid: `existing-${c.file_id || c.id || Math.random()}`,
          name,
          status: 'done' as const,
          size,
          file_path: fileObj?.url || c.file_path,
          fileId: c.file_id,
          created_at: c.created_at || fileObj?.created_at,
          isExisting: true,
          file_size: size,
          file_format: c.mime_type || fileObj?.mime_type,
        } as UploadFile;
      });
      setFileList(existingFiles);
    } else if (
      Array.isArray(initialDocuments) &&
      initialDocuments.length === 0
    ) {
      setDocuments((prev) => (prev.length === 0 ? prev : []));
      setFileList([]);
    }
  }, [initialDocuments]);

  const handleFileListChange = (newFileList: UploadFile[]) => {
    setFileList(newFileList);

    setDocuments((prev) => {
      const existingFileIds = new Set(
        prev.map((c) => c.file_id).filter(Boolean)
      );
      const existingFileNames = new Set(
        prev.map((c) => c.file_name?.toLowerCase()).filter(Boolean)
      );

      const pendingDocuments: ListingContractDocument[] = newFileList
        .filter((f) => {
          if (!(f as any).originFileObj) {
            return false;
          }

          const fileId = (f as CustomUploadFile).fileId;
          const fileName = f.name?.toLowerCase();

          if (fileId && existingFileIds.has(fileId)) {
            return false;
          }

          if (fileName && existingFileNames.has(fileName)) {
            return false;
          }

          return true;
        })
        .map((file) => ({
          id: Number(file.uid) || Date.now(),
          file_name: file.name,
          file_size: file.size,
          file_format: (file.name?.split('.').pop() || '').toLowerCase(),
          created_at: new Date().toISOString(),
          originFile: (file as any).originFileObj,
          isPending: true,
        }));

      const updated = [...prev, ...pendingDocuments];

      Promise.resolve().then(() => {
        onDocumentsChange?.(updated);
      });

      return updated;
    });
  };

  const handleFileRemove = (file: UploadFile) => {
    const newFileList = fileList.filter((item) => item.uid !== file.uid);
    setFileList(newFileList);

    const existingFileId = (file as CustomUploadFile).fileId as
      | number
      | undefined;

    setDocuments((prev) => {
      const updated = prev.filter((doc) => {
        if (existingFileId && doc.file_id) {
          return doc.file_id !== existingFileId;
        }
        const fileObj = doc.file;
        const name = doc.file_name || fileObj?.original_name || fileObj?.name;
        return name !== file.name;
      });

      Promise.resolve().then(() => {
        onDocumentsChange?.(updated);
      });

      return updated;
    });

    if (onDeleteFile) {
      onDeleteFile(file);
    }
  };

  const handleFileDownload = (file: UploadFile) => {
    const f = file as CustomUploadFile;
    const listId = listingId;

    if (listId && f.fileId) {
      const url = `${routes.api.baseUrl}/api/v2/listings/${listId}/contract-terms/download?file_id=${f.fileId}`;
      window.open(url, '_blank');

      return;
    }

    const matched = documents.find((d) => {
      if (f.fileId && d.file_id === f.fileId) {
        return true;
      }

      const docName = d.file_name || d.file?.original_name || d.file?.name;
      if (docName === file.name) {
        return true;
      }

      if (d.id && d.id.toString() === file.uid) {
        return true;
      }
      return false;
    });

    if (listId && matched?.file_id) {
      const url = `${routes.api.baseUrl}/api/v2/listings/${listId}/contract-terms/download?file_id=${matched.file_id}`;
      window.open(url, '_blank');

      return;
    }

    const blob =
      (file as any).originFileObj ||
      (file as any).originFile ||
      matched?.originFile;

    if (blob && blob instanceof File) {
      try {
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;

        const downloadName =
          blob.name || matched?.file_name || file.name || 'download';
        a.download = downloadName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objectUrl);

        return;
      } catch (error) {}
    }

    message.warning(
      intl.formatMessage({ id: 'file_uploader.download_unavailable' })
    );
  };

  return (
    <StyledNewListingContractTermsForm>
      <div className={'contract-terms'}>
        <div className={'row'}>
          <div className={'date'}>
            <DatePicker
              name={'contract_term_start_date'}
              label={intl.formatMessage({
                id: 'listings.form.contract_start_date',
              })}
              validations={'required'}
              needConfirm={false}
            />
          </div>
          <div className={'date'}>
            <DatePicker
              name={'contract_term_end_date'}
              label={intl.formatMessage({
                id: 'listings.form.contract_end_date',
              })}
              validations={'required'}
              needConfirm={false}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const startDate = getFieldValue('contract_term_start_date');
                    if (!value || !startDate) {
                      return Promise.resolve();
                    }
                    if (value.isBefore(startDate) || value.isSame(startDate)) {
                      return Promise.reject(
                        new Error(
                          intl.formatMessage({
                            id: 'validation.contract_end_after_start',
                          })
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            />
          </div>
        </div>
        <TextArea
          className={'textarea'}
          name={'payment_conditions'}
          label={intl.formatMessage({
            id: 'listings.form.payment_conditions',
          })}
          maxLength={2000}
        />
        <TextArea
          className={'textarea'}
          name={'additional_contract_terms'}
          label={intl.formatMessage({
            id: 'listings.form.additional_contract_terms',
          })}
          maxLength={2000}
        />
        <FileUploader
          name={'contract_documents'}
          label={intl.formatMessage({
            id: 'listings.form.add_contract_template',
          })}
          fileList={fileList}
          maxFiles={0}
          onFileListChange={handleFileListChange}
          onDownload={handleFileDownload}
          onDelete={handleFileRemove}
          acceptedTypes={['.pdf', '.xlsx', '.docx']}
          multiple={true}
        />
      </div>
    </StyledNewListingContractTermsForm>
  );
};
